/**
 * The tenant's saas-auth brand document (CNPWKS-0007): types, validation, fetch, cache and sync.
 *
 * `GET {authOrigin}/api/branding/theme?host=<hostname>` is public. It is a plain `fetch` with
 * `credentials: 'omit'` and `Accept: application/json` only: no bearer, CSRF, request id or
 * `If-None-Match` (C1), and never through `apiClient`. A document is validated before use and
 * rejected WHOLE when any rule fails; every value Emotion writes verbatim passes its pattern first.
 */
import platformDefault from './fixtures/platform-default.v1.json';

export const BRAND_THEME_SCHEMA_VERSION = 1;

// prettier-ignore
export const ROLE_NAMES = [
  'bg', 'surface', 'surface2', 'surfaceRaised', 'text', 'text2', 'text3', 'link', 'line',
  'lineStrong', 'hover', 'selected', 'focusRing', 'brand', 'brandHover', 'brandInk', 'brandSoft',
  'brandDeep', 'brandDeepInk', 'brandDeepMuted', 'brandBoundary', 'accent', 'accentHover',
  'accentInk', 'accentSoft', 'appBar', 'appBarInk', 'appBarAccent',
  'successFg', 'successText', 'successFill', 'successInk', 'successSoft',
  'warningFg', 'warningText', 'warningFill', 'warningInk', 'warningSoft',
  'errorFg', 'errorText', 'errorFill', 'errorInk', 'errorSoft',
  'infoFg', 'infoText', 'infoFill', 'infoInk', 'infoSoft',
  'moneyPositive', 'moneyNegative', 'moneyNegativeWash', 'highlight', 'scrim',
] as const;
export type RoleName = (typeof ROLE_NAMES)[number];
export type BrandMode = 'light' | 'dark';
export type BrandRoles = Record<RoleName, string>;

/** Served as #rrggbbaa; every other role is #rrggbb. */
export const TRANSLUCENT_ROLES: readonly RoleName[] = ['hover', 'selected', 'scrim'];
/**
 * May be absent: scrim → the built-in default's value for the mode; brandBoundary → the doc's own
 * link; `appBarAccent` → the doc's own `appBarInk` (saas-auth REPLY 2026-10-12).
 */
export const OPTIONAL_ROLES: readonly RoleName[] = ['scrim', 'brandBoundary', 'appBarAccent'];

export interface Radius {
  style: string;
  control: number;
  card: number;
  dialog: number;
}
export interface BrandFont {
  family: string;
  stack: string;
  source: 'google' | 'system';
  cssUrl: string | null;
  weights: number[];
}
export interface BrandAssets {
  logo: string | null;
  logoReversed: string | null;
  mark: string | null;
  favicon: { svg: string | null; png: string | null };
}
export const COPY_KEYS = [
  'productName',
  'productShortName',
  'loginTitle',
  'tagline',
  'heroCopy',
  'location',
  'welcomeHeadline',
  'supportEmail',
] as const;
export type BrandCopyStrings = Record<(typeof COPY_KEYS)[number], string | null>;
export interface BrandTheme {
  schemaVersion: 1;
  themeVersion: string;
  source: string; // 'brand' | 'platform-default'; other values are treated as 'brand'
  modes: Record<BrandMode, BrandRoles>;
  radius: Radius;
  fonts: { body: BrandFont; heading: BrandFont; mono: BrandFont };
  assets: BrandAssets;
  copy: { defaultLocale: string; locales: Record<string, BrandCopyStrings> };
  contrast: { ok: boolean; failures: unknown[] };
}
export type BrandThemeValidation = { ok: true; theme: BrandTheme } | { ok: false; reason: string };

const OPAQUE = /^#[0-9a-f]{6}$/i;
const TRANSLUCENT = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;
const CSS_BREAKOUT = /[;{}<>\\]|url\(/i;
const GOOGLE_FONTS_ORIGIN = 'https://fonts.googleapis.com';
const MODES: readonly BrandMode[] = ['light', 'dark'];
const FONT_SLOTS = ['body', 'heading', 'mono'] as const;

/** Thrown inside validation to reject the whole document; caught in validateBrandTheme. */
class Rejection extends Error {}

function reject(reason: string): never {
  throw new Rejection(reason);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function parseRoles(mode: BrandMode, raw: unknown): BrandRoles {
  if (!isRecord(raw)) reject(`modes.${mode} is not an object`);
  const roles = {} as BrandRoles;
  for (const role of ROLE_NAMES) {
    const value = raw[role];
    if ((value === undefined || value === null) && OPTIONAL_ROLES.includes(role)) continue;
    if (value === undefined || value === null) reject(`missing role '${role}' in mode '${mode}'`);
    const pattern = TRANSLUCENT_ROLES.includes(role) ? TRANSLUCENT : OPAQUE;
    if (typeof value !== 'string' || !pattern.test(value)) {
      reject(`invalid colour for role '${role}' in mode '${mode}'`);
    }
    roles[role] = value.toLowerCase();
  }
  roles.brandBoundary ??= roles.link;
  roles.appBarAccent ??= roles.appBarInk;
  roles.scrim ??= platformDefault.modes[mode].scrim;
  return roles;
}

function parseRadius(raw: unknown): Radius {
  if (!isRecord(raw)) reject('radius is not an object');
  const size = (key: 'control' | 'card' | 'dialog'): number => {
    const value = raw[key];
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
      reject(`invalid radius.${key}`);
    }
    return value;
  };
  return {
    style: typeof raw.style === 'string' ? raw.style : 'rounded',
    control: size('control'),
    card: size('card'),
    dialog: size('dialog'),
  };
}

function googleCssUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    return new URL(value).origin === GOOGLE_FONTS_ORIGIN ? value : null;
  } catch {
    return null;
  }
}

function parseFont(slot: string, raw: unknown): BrandFont {
  if (!isRecord(raw)) reject(`fonts.${slot} is not an object`);
  for (const key of ['family', 'stack'] as const) {
    const value = raw[key];
    if (!isNonEmptyString(value) || CSS_BREAKOUT.test(value))
      reject(`invalid fonts.${slot}.${key}`);
  }
  const cssUrl = googleCssUrl(raw.cssUrl);
  return {
    family: raw.family as string,
    stack: raw.stack as string,
    source: raw.source === 'google' && cssUrl !== null ? 'google' : 'system',
    cssUrl,
    weights: Array.isArray(raw.weights)
      ? raw.weights.filter((w): w is number => typeof w === 'number' && Number.isFinite(w))
      : [],
  };
}

/** null/absent → null; an https: URL → kept; anything else rejects the document (handoff §3.2). */
function parseAssetUrl(path: string, value: unknown): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string') {
    try {
      if (new URL(value).protocol === 'https:') return value;
    } catch {
      // fall through to the rejection
    }
  }
  return reject(`invalid asset URL at assets.${path}`);
}

function parseAssets(raw: unknown): BrandAssets {
  if (!isRecord(raw)) reject('assets is not an object');
  if (!isRecord(raw.favicon)) reject('assets.favicon is not an object');
  return {
    logo: parseAssetUrl('logo', raw.logo),
    logoReversed: parseAssetUrl('logoReversed', raw.logoReversed),
    mark: parseAssetUrl('mark', raw.mark),
    favicon: {
      svg: parseAssetUrl('favicon.svg', raw.favicon.svg),
      png: parseAssetUrl('favicon.png', raw.favicon.png),
    },
  };
}

function parseCopy(raw: unknown): BrandTheme['copy'] {
  if (!isRecord(raw)) reject('copy is not an object');
  if (!isNonEmptyString(raw.defaultLocale)) reject('invalid copy.defaultLocale');
  if (!isRecord(raw.locales)) reject('copy.locales is not an object');
  const locales: Record<string, BrandCopyStrings> = {};
  for (const [locale, strings] of Object.entries(raw.locales)) {
    if (!isRecord(strings)) reject(`copy.locales.${locale} is not an object`);
    const parsed = {} as BrandCopyStrings;
    for (const key of COPY_KEYS) {
      const value = strings[key];
      if (value === undefined || value === null) parsed[key] = null;
      else if (typeof value === 'string') parsed[key] = value;
      else reject(`invalid copy.locales.${locale}.${key}`);
    }
    locales[locale] = parsed;
  }
  return { defaultLocale: raw.defaultLocale, locales };
}

function parseContrast(raw: unknown): BrandTheme['contrast'] {
  if (!isRecord(raw) || typeof raw.ok !== 'boolean') reject('invalid contrast.ok');
  return { ok: raw.ok, failures: Array.isArray(raw.failures) ? raw.failures : [] };
}

function parse(json: unknown): BrandTheme {
  if (!isRecord(json)) reject('the document is not an object');
  if (json.schemaVersion !== BRAND_THEME_SCHEMA_VERSION) {
    reject(`unsupported schemaVersion ${String(json.schemaVersion)}`);
  }
  if (!isNonEmptyString(json.themeVersion)) reject('invalid themeVersion');
  if (!isNonEmptyString(json.source)) reject('invalid source');
  if (!isRecord(json.modes)) reject('modes is not an object');
  const modes = {} as Record<BrandMode, BrandRoles>;
  for (const mode of MODES) modes[mode] = parseRoles(mode, json.modes[mode]);
  if (!isRecord(json.fonts)) reject('fonts is not an object');
  const fonts = json.fonts;
  const [body, heading, mono] = FONT_SLOTS.map((slot) => parseFont(slot, fonts[slot])) as [
    BrandFont,
    BrandFont,
    BrandFont,
  ];
  return {
    schemaVersion: 1,
    themeVersion: json.themeVersion,
    source: json.source,
    modes,
    radius: parseRadius(json.radius),
    fonts: { body, heading, mono },
    assets: parseAssets(json.assets),
    copy: parseCopy(json.copy),
    contrast: parseContrast(json.contrast),
  };
}

export function validateBrandTheme(json: unknown): BrandThemeValidation {
  try {
    return { ok: true, theme: parse(json) };
  } catch (e) {
    if (e instanceof Rejection) return { ok: false, reason: e.message };
    return { ok: false, reason: e instanceof Error ? e.message : String(e) };
  }
}

export function normalizeBrandTheme(json: unknown): BrandTheme | null {
  const result = validateBrandTheme(json);
  return result.ok ? result.theme : null;
}

/** WCAG 2.x relative luminance of a validated opaque role (six hex digits). */
function relativeLuminance(hex: string): number {
  // The three slices are always present; the assertion satisfies noUncheckedIndexedAccess.
  const [r, g, b] = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * True when the app bar is dark: its ink is lighter than its fill. Picks the reversed logo
 * (handoff §3.5); it chooses an asset and derives no colour.
 */
export function isDarkAppBar(roles: Pick<BrandRoles, 'appBar' | 'appBarInk'>): boolean {
  return relativeLuminance(roles.appBarInk) > relativeLuminance(roles.appBar);
}

const FETCH_TIMEOUT_MS = 5000;

export type BrandThemeFetchResult =
  { status: 'ok'; theme: BrandTheme } | { status: 'failed'; reason: string };

/** One public request (C1). The ETag header is never read; the browser's HTTP cache revalidates. */
export async function fetchBrandTheme(
  authOrigin: string,
  host: string,
): Promise<BrandThemeFetchResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    let response: Response;
    try {
      response = await fetch(`${authOrigin}/api/branding/theme?host=${encodeURIComponent(host)}`, {
        credentials: 'omit',
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
    } catch (e) {
      return {
        status: 'failed',
        reason: `fetch failed: ${e instanceof Error ? e.message : String(e)}`,
      };
    }

    let body: unknown;
    let parsed = true;
    try {
      body = await response.json();
    } catch {
      parsed = false;
    }

    if (!response.ok) {
      const code = isRecord(body) && isRecord(body.error) ? body.error.code : undefined;
      return {
        status: 'failed',
        reason: `fetch failed: ${response.status}${typeof code === 'string' ? ` ${code}` : ''}`,
      };
    }
    if (!parsed) return { status: 'failed', reason: 'rejected: the body is not JSON' };

    const result = validateBrandTheme(body);
    return result.ok
      ? { status: 'ok', theme: result.theme }
      : { status: 'failed', reason: `rejected: ${result.reason}` };
  } finally {
    clearTimeout(timer);
  }
}

/** Public, non-personal brand data, cached per host (C3). Never auth data. */
export const BRAND_THEME_STORAGE_KEY = 'cnpwks_brand_theme';

/** Re-validates the cached `doc`; an entry for another host or a bad entry → null. */
export function readCachedBrandTheme(host: string): BrandTheme | null {
  try {
    const raw = localStorage.getItem(BRAND_THEME_STORAGE_KEY);
    if (raw === null) return null;
    const entry: unknown = JSON.parse(raw);
    if (!isRecord(entry) || entry.host !== host) return null;
    return normalizeBrandTheme(entry.doc);
  } catch {
    return null;
  }
}

export function writeCachedBrandTheme(host: string, theme: BrandTheme): void {
  try {
    localStorage.setItem(
      BRAND_THEME_STORAGE_KEY,
      JSON.stringify({ host, themeVersion: theme.themeVersion, doc: theme }),
    );
  } catch {
    // Storage blocked or full: the theme still applies for this page load.
  }
}

/** Quiet in production (C13): false when either value is `production` or `prod`. */
export function brandDiagnosticsEnabled(buildEnv: string, bootstrapEnv: string | null): boolean {
  const isProduction = (value: string | null) => {
    const v = value?.trim().toLowerCase();
    return v === 'production' || v === 'prod';
  };
  return !isProduction(buildEnv) && !isProduction(bootstrapEnv);
}

const warnedKinds = new Set<string>();
let lastContrastWarned: string | null = null;

/** One warning per failure kind (the text before the first `:`) per page load. */
function warnFailure(reason: string): void {
  const kind = reason.split(':')[0] ?? reason;
  if (warnedKinds.has(kind)) return;
  warnedKinds.add(kind);
  console.warn(`[brand-theme] ${reason}`);
}

function warnContrast(theme: BrandTheme): void {
  if (theme.contrast.ok || lastContrastWarned === theme.themeVersion) return;
  lastContrastWarned = theme.themeVersion;
  console.warn(
    `[brand-theme] contrast failures in theme ${theme.themeVersion}`,
    theme.contrast.failures,
  );
}

export function __resetBrandThemeDiagnosticsForTest(): void {
  warnedKinds.clear();
  lastContrastWarned = null;
}

export interface SyncBrandThemeOptions {
  /** The longest a first visit (no cache) waits for the fetch (C4). */
  waitMs?: number;
  diagnostics?: boolean;
}

/**
 * Fetches the brand once, caches it, and applies it when its version differs from `current` (C5).
 * With a cached copy for this host it returns at once; otherwise it waits at most `waitMs`. It
 * never rejects and never retries.
 */
export async function syncBrandTheme(
  authOrigin: string,
  host: string,
  current: string | null,
  apply: (theme: BrandTheme) => void,
  { waitMs = 1500, diagnostics = false }: SyncBrandThemeOptions = {},
): Promise<void> {
  const cached = readCachedBrandTheme(host);
  if (cached && diagnostics) warnContrast(cached);

  const request = fetchBrandTheme(authOrigin, host)
    .then((result) => {
      if (result.status === 'failed') {
        if (diagnostics) warnFailure(result.reason);
        return;
      }
      writeCachedBrandTheme(host, result.theme);
      if (result.theme.themeVersion !== current) {
        if (diagnostics) warnContrast(result.theme);
        apply(result.theme);
      }
    })
    .catch(() => undefined);

  if (cached) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, waitMs);
  });
  await Promise.race([request, timeout]);
  clearTimeout(timer);
}
